import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";
import { notification } from "antd";
import { useSelector } from "react-redux";
import { selectIsCuvamaUser } from "../store/reducers";
import { useFeature } from "../components/FeaturesProvider";
import Features from "../utils/constants/Features";
import integrationsService from "../services/integrations.service";
import type { ApiTypes } from "cuvama-api";

interface AgentApiKeyContextType {
  agentApiKey: ApiTypes.ApiKey | null;
  loading: boolean;
  setupLoading: boolean;
  regenerateLoading: boolean;
  refresh: () => Promise<void>;
  setupAgent: () => Promise<ApiTypes.ApiKey | null>;
  regenerateAgentApiKey: () => Promise<ApiTypes.ApiKey | null>;
}

const defaultContext: AgentApiKeyContextType = {
  agentApiKey: null,
  loading: false,
  setupLoading: false,
  regenerateLoading: false,
  refresh: async () => {},
  setupAgent: async () => null,
  regenerateAgentApiKey: async () => null
};

const AgentApiKeyContext =
  createContext<AgentApiKeyContextType>(defaultContext);

export const useAgentApiKey = (): AgentApiKeyContextType =>
  useContext(AgentApiKeyContext);

interface AgentApiKeyProviderProps {
  children: React.ReactNode;
}

const AgentApiKeyProvider = ({ children }: AgentApiKeyProviderProps) => {
  const isCuvamaUser = useSelector(selectIsCuvamaUser);
  const cuvamaDiscoveryEnabled = useFeature(
    Features.CUVAMA_DISCOVERY_INTEGRATION,
    false
  );

  const enabled = Boolean(isCuvamaUser && cuvamaDiscoveryEnabled);

  const [agentApiKey, setAgentApiKey] = useState<ApiTypes.ApiKey | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [setupLoading, setSetupLoading] = useState<boolean>(false);
  const [regenerateLoading, setRegenerateLoading] = useState<boolean>(false);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setAgentApiKey(null);
      return;
    }

    setLoading(true);
    try {
      const data = await integrationsService.getAgentApiKey();
      setAgentApiKey(data ?? null);
    } catch (error) {
      notification.error({
        message: "Failed to load the agent API key"
      });
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setupAgent = useCallback(async () => {
    if (!enabled) return null;

    setSetupLoading(true);
    try {
      const data = await integrationsService.setupAgent({ regenerate: false });
      setAgentApiKey(data ?? null);
      return data ?? null;
    } finally {
      setSetupLoading(false);
    }
  }, [enabled]);

  const regenerateAgentApiKey = useCallback(async () => {
    if (!enabled) return null;

    setRegenerateLoading(true);
    try {
      const data = await integrationsService.setupAgent({ regenerate: true });
      setAgentApiKey(data ?? null);
      return data ?? null;
    } finally {
      setRegenerateLoading(false);
    }
  }, [enabled]);

  return (
    <AgentApiKeyContext.Provider
      value={{
        agentApiKey,
        loading,
        setupLoading,
        regenerateLoading,
        refresh,
        setupAgent,
        regenerateAgentApiKey
      }}
    >
      {children}
    </AgentApiKeyContext.Provider>
  );
};

export default AgentApiKeyProvider;
